import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Box, Button, TextField, Typography, Alert, MenuItem } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ProjectFormData, Client, Employee } from '../../types';
import { projectsAPI, clientsAPI, employeesAPI } from '../../services/api';
import { NAVIGATION, VALIDATION_MESSAGES, CURRENCIES } from '../../constants';
import { useProject } from '../../hooks/useProjects';
import FileUpload from '../../components/files/FileUpload';
import { useFiles } from '../../hooks/useFiles';
import ProjectEmployeeManager from '../../components/projects/ProjectEmployeeManager';

const schema = z.object({
  name: z.string().min(2, VALIDATION_MESSAGES.MIN_LENGTH(2)),
  description: z.string().optional(),
  clientId: z.string().min(1, VALIDATION_MESSAGES.REQUIRED),
  currency: z.string().min(1, VALIDATION_MESSAGES.REQUIRED),
  startDate: z.string().min(1, VALIDATION_MESSAGES.REQUIRED),
  endDate: z.string().optional(),
  terminationDate: z.string().optional(),
  employees: z.array(z.object({
    employeeId: z.string(),
    role: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    price: z.number().optional(),
    unit: z.string().optional(),
    shadow: z.boolean()
  })).optional(),
});

const EditProject: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { files, uploadFile, deleteFile } = useFiles('PROJECT', id!);
  
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, watch } = useForm<ProjectFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      clientId: '',
      currency: CURRENCIES.EUR,
      startDate: '',
      endDate: '',
      terminationDate: '',
      employees: [],
    },
    mode: 'onChange'
  });
  const [success, setSuccess] = React.useState('');
  const [error, setError] = React.useState('');
  const [originalData, setOriginalData] = React.useState<ProjectFormData | null>(null);
  const [projectEmployees, setProjectEmployees] = React.useState<ProjectFormData['employees']>([]);
  
  const { project, isLoading: projectLoading } = useProject(id!);
  
  const watchedValues = watch();
  const hasChanges = originalData && (
    watchedValues.name !== originalData.name ||
    watchedValues.description !== originalData.description ||
    watchedValues.clientId !== originalData.clientId ||
    watchedValues.currency !== originalData.currency ||
    watchedValues.startDate !== originalData.startDate ||
    watchedValues.endDate !== originalData.endDate ||
    watchedValues.terminationDate !== originalData.terminationDate ||
    JSON.stringify(projectEmployees) !== JSON.stringify(originalData.employees)
  );

  // Fetch clients and employees for dropdowns
  const { data: clients } = useQuery<Client[]>({
    queryKey: ['clients'],
    queryFn: clientsAPI.getAll
  });

  const { data: employees } = useQuery<Employee[]>({
    queryKey: ['employees'],
    queryFn: employeesAPI.getAll
  });

  useEffect(() => {
    if (project) {
      const formData = {
        name: project.name || '',
        description: project.description || '',
        clientId: project.client?.id || '',
        currency: project.currency || CURRENCIES.EUR,
        startDate: project.startDate ? project.startDate.substring(0, 10) : '',
        endDate: project.endDate ? project.endDate.substring(0, 10) : '',
        terminationDate: project.terminationDate ? project.terminationDate.substring(0, 10) : '',
        employees: project.employees?.map((emp: any) => ({
          employeeId: emp.id,
          role: emp.role,
          startDate: emp.startDate ? emp.startDate.substring(0, 10) : undefined,
          endDate: emp.endDate ? emp.endDate.substring(0, 10) : undefined,
          price: emp.price,
          unit: emp.unit,
          shadow: emp.shadow || false
        })) || [],
      };
      reset(formData);
      setOriginalData(formData);
      setProjectEmployees(formData.employees || []);
    }
  }, [project, reset]);

  const onSubmit = async (data: ProjectFormData) => {
    setSuccess(''); setError('');
    try {
      const payload: any = {
        name: data.name,
        description: data.description || '', // Always include description, even if empty
        clientId: data.clientId,
        currency: data.currency,
        startDate: data.startDate ? new Date(data.startDate).toISOString() : undefined,
        endDate: data.endDate ? new Date(data.endDate).toISOString() : undefined,
        terminationDate: data.terminationDate ? new Date(data.terminationDate).toISOString() : undefined,
        employees: (projectEmployees || []).map(emp => ({
          ...emp,
          startDate: emp.startDate ? new Date(emp.startDate).toISOString() : undefined,
          endDate: emp.endDate ? new Date(emp.endDate).toISOString() : undefined,
          price: emp.price !== null && emp.price !== undefined ? emp.price : undefined,
        })),
      };

      await projectsAPI.update(id!, payload);
      setSuccess('Project updated successfully!');
      navigate(NAVIGATION.PROJECTS);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update project');
    }
  };

  if (projectLoading || !originalData || !clients || !employees) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: { xs: 2, md: 0 } }}>
        <Typography variant="h5" fontWeight={700} mb={2}>Edit Project</Typography>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: { xs: 2, md: 0 } }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Edit Project</Typography>
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            {clients ? (
              <TextField
                label="Client"
                select
                fullWidth
                margin="normal"
                {...register('clientId')}
                error={!!errors.clientId}
                helperText={errors.clientId?.message}
                InputLabelProps={{ shrink: true }}
                value={watch('clientId') || ''}
              >
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                label="Client"
                fullWidth
                margin="normal"
                value="Loading clients..."
                disabled
                InputLabelProps={{ shrink: true }}
              />
            )}
            <TextField label="Name" fullWidth margin="normal" {...register('name')} error={!!errors.name} helperText={errors.name?.message} InputLabelProps={{ shrink: true }} />
            <TextField label="Currency" select fullWidth margin="normal" {...register('currency')} error={!!errors.currency} helperText={errors.currency?.message} InputLabelProps={{ shrink: true }} value={watch('currency') || CURRENCIES.EUR}>
              <MenuItem value={CURRENCIES.EUR}>EUR (€)</MenuItem>
              <MenuItem value={CURRENCIES.USD}>USD ($)</MenuItem>
              <MenuItem value={CURRENCIES.GBP}>GBP (£)</MenuItem>
              <MenuItem value={CURRENCIES.RSD}>RSD (din)</MenuItem>
              <MenuItem value={CURRENCIES.CHF}>CHF (CHF)</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Start Date" type="date" fullWidth margin="normal" {...register('startDate')} error={!!errors.startDate} helperText={errors.startDate?.message} InputLabelProps={{ shrink: true }} />
            <TextField label="End Date" type="date" fullWidth margin="normal" {...register('endDate')} error={!!errors.endDate} helperText={errors.endDate?.message} InputLabelProps={{ shrink: true }} />
            <TextField label="Termination Date" type="date" fullWidth margin="normal" {...register('terminationDate')} error={!!errors.terminationDate} helperText={errors.terminationDate?.message} InputLabelProps={{ shrink: true }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField 
              label="Description" 
              fullWidth 
              margin="normal" 
              multiline
              rows={4}
              {...register('description')} 
              error={!!errors.description} 
              helperText={errors.description?.message} 
              InputLabelProps={{ shrink: true }} 
            />
          </Box>
          
          <ProjectEmployeeManager
            employees={employees || []}
            projectEmployees={projectEmployees}
            onEmployeesChange={setProjectEmployees}
            disabled={isSubmitting}
            projectCurrency={watch('currency') || CURRENCIES.EUR}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" mb={2}>Files</Typography>
          <Box sx={{ width: '100%', overflow: 'auto' }}>
            <FileUpload
              entityType="PROJECT"
              entityId={id!}
              files={files}
              uploadFile={uploadFile}
              deleteFile={deleteFile}
              onFilesChange={() => {}} // Files are managed by useFiles hook
            />
          </Box>
        </Box>

        <Button 
          type="button" 
          variant="contained" 
          color="primary" 
          fullWidth 
          sx={{ mt: 2 }} 
          disabled={isSubmitting || !hasChanges}
          onClick={async () => {
            const currentValues = watch();
            try {
              const payload: any = {
                name: currentValues.name,
                description: currentValues.description || '', // Always include description, even if empty
                clientId: currentValues.clientId,
                currency: currentValues.currency,
                startDate: currentValues.startDate ? new Date(currentValues.startDate).toISOString() : undefined,
                endDate: currentValues.endDate ? new Date(currentValues.endDate).toISOString() : undefined,
                terminationDate: currentValues.terminationDate ? new Date(currentValues.terminationDate).toISOString() : undefined,
                employees: (projectEmployees || []).map(emp => ({
          ...emp,
          startDate: emp.startDate ? new Date(emp.startDate).toISOString() : undefined,
          endDate: emp.endDate ? new Date(emp.endDate).toISOString() : undefined,
          price: emp.price !== null && emp.price !== undefined ? emp.price : undefined,
        })),
              };

              await projectsAPI.update(id!, payload);
              setSuccess('Project updated successfully!');
              navigate(NAVIGATION.PROJECTS);
            } catch (err: any) {
              console.error('Error updating project:', err);
              setError(err.response?.data?.error || 'Failed to update project');
            }
          }}
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </form>
    </Box>
  );
};

export default EditProject; 